import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Loader2, Droplets, Scissors, Wheat, Eye, MoreHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import { API_BASE } from "./constants";
import ModalPortal from "@/components/ui/modal-portal";

const CHANTIER_TYPES = [
  { value: "irrigation", label: "Irrigation", icon: Droplets, color: "#0891B2" },
  { value: "taille", label: "Taille", icon: Scissors, color: "#7C3AED" },
  { value: "recolte", label: "Récolte", icon: Wheat, color: "#D97706" },
  { value: "observation", label: "Observation", icon: Eye, color: "#059669" },
  { value: "autre", label: "Autre", icon: MoreHorizontal, color: "#6B7280" },
];

export default function ChantiersTab({ parcelId, parcelName, ilotId, ilots = [] }) {
  const ilotName = (id) => (ilots.find((i) => i.ilot_id === id) || {}).name;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    chantier_type: "irrigation",
    quantity_value: "",
    quantity_unit: "",
    operator_name: "",
    equipment: "",
    notes: "",
  });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    const res = await authFetch(`${API_BASE}/chantiers/?parcel_id=${parcelId}${ilotId ? `&ilot_id=${ilotId}` : ""}`);
    if (res.ok) setItems(await res.json());
    setLoading(false);
  }, [parcelId, ilotId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const submit = async () => {
    setSaving(true);
    const res = await authFetch(`${API_BASE}/chantiers/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        parcel_id: parcelId,
        ilot_id: ilotId || null,
        date: form.date,
        chantier_type: form.chantier_type,
        quantity_value: form.quantity_value ? parseFloat(form.quantity_value) : null,
        quantity_unit: form.quantity_unit || null,
        operator_name: form.operator_name || null,
        equipment: form.equipment || null,
        notes: form.notes || null,
      }),
    });
    setSaving(false);
    if (res.ok) {
      toast.success("Chantier enregistré");
      setShowForm(false);
      setForm({ date: new Date().toISOString().slice(0, 10), chantier_type: "irrigation", quantity_value: "", quantity_unit: "", operator_name: "", equipment: "", notes: "" });
      fetchItems();
    } else {
      toast.error("Erreur d'enregistrement");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce chantier ?")) return;
    const res = await authFetch(`${API_BASE}/chantiers/${id}`, { method: "DELETE" });
    if (res.ok) { toast.success("Supprimé"); fetchItems(); } else toast.error("Erreur");
  };

  return (
    <div className="h-full flex flex-col p-3 sm:p-4 overflow-y-auto" style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }} data-testid="chantiers-tab">
      <div className="mb-3 p-2 rounded-md bg-gray-50 border border-gray-200 text-[11px] text-gray-700 flex items-start gap-2">
        <span>Journal des opérations culturales <b>non phytosanitaires</b> (irrigation, taille, récolte, observation). Non soumis à l'arrêté du 24 déc 2025.</span>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-sm font-bold flex-1" style={{ color: "#2D6A4F" }}>Chantiers — {parcelName}</h2>
        <Button size="sm" className="h-8 text-xs bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => setShowForm(true)} data-testid="new-chantier-btn">
          <Plus className="w-3 h-3 mr-1" />Nouveau chantier
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#2D6A4F" }} /></div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center text-gray-500 text-sm">Aucun chantier enregistré</div>
      ) : (
        <div className="space-y-2" data-testid="chantiers-list">
          {items.map(c => {
            const meta = CHANTIER_TYPES.find(t => t.value === c.chantier_type) || CHANTIER_TYPES[4];
            const Icon = meta.icon;
            return (
              <div key={c.chantier_id} className="p-3 rounded-lg border bg-white flex items-start gap-3" data-testid={`chantier-${c.chantier_id}`}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${meta.color}15` }}>
                  <Icon className="w-4 h-4" style={{ color: meta.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase" style={{ color: meta.color }}>{meta.label}</span>
                    <span className="text-[11px] text-gray-500">{c.date}</span>
                    {c.ilot_id && ilotName(c.ilot_id) && <span className="text-[9px] font-bold rounded-full px-1.5 py-0.5" style={{ background: "#dbe8f1", color: "#2f6a92" }}>{ilotName(c.ilot_id)}</span>}
                  </div>
                  <div className="text-[11px] text-gray-600 mt-0.5 flex flex-wrap gap-x-3">
                    {c.quantity_value && <span>{c.quantity_value} {c.quantity_unit || ""}</span>}
                    {c.operator_name && <span>👤 {c.operator_name}</span>}
                    {c.equipment && <span>🛠 {c.equipment}</span>}
                  </div>
                  {c.notes && <p className="text-[11px] text-gray-500 mt-1 italic">{c.notes}</p>}
                </div>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:bg-red-50" onClick={() => remove(c.chantier_id)} data-testid={`del-chantier-${c.chantier_id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <ModalPortal>
        <div className="fixed inset-0 z-[2000] bg-black/50 flex items-center justify-center p-2 sm:p-4" data-testid="chantier-form-modal">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[95dvh] overflow-hidden flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-emerald-700 to-emerald-800 text-white">
              <h3 className="text-sm font-bold">Nouveau chantier</h3>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-white hover:bg-white/20" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase">Date *</Label>
                  <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="h-9 text-sm" data-testid="c-date" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase">Type *</Label>
                  <Select value={form.chantier_type} onValueChange={v => setForm({ ...form, chantier_type: v })}>
                    <SelectTrigger className="h-9 text-sm" data-testid="c-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CHANTIER_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase">Quantité</Label>
                  <Input type="number" step="any" value={form.quantity_value} onChange={e => setForm({ ...form, quantity_value: e.target.value })} placeholder={form.chantier_type === "irrigation" ? "ex: 30" : ""} className="h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase">Unité</Label>
                  <Input value={form.quantity_unit} onChange={e => setForm({ ...form, quantity_unit: e.target.value })} placeholder={form.chantier_type === "irrigation" ? "m³/ha" : "kg, t..."} className="h-9 text-sm" />
                </div>
              </div>
              <Input value={form.operator_name} onChange={e => setForm({ ...form, operator_name: e.target.value })} placeholder="Opérateur" className="h-9 text-sm" />
              <Input value={form.equipment} onChange={e => setForm({ ...form, equipment: e.target.value })} placeholder="Matériel utilisé" className="h-9 text-sm" />
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notes / observations" rows={3} className="text-sm" />
            </div>
            <div className="border-t p-3 flex justify-end gap-2 bg-gray-50">
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)} className="text-xs">Annuler</Button>
              <Button size="sm" onClick={submit} disabled={saving} className="text-xs bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-chantier-btn">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}Enregistrer
              </Button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}
    </div>
  );
}
